import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { createCurvedLamellaGeometry } from '../../../containers/Fiber/3dObjects/curvedLamellaGeometry';

// Generates N points along a circular arc in the X-Y plane.
// Arc spans beam length along X, camber rises toward +Y at midspan (y=0 at ends, y=sagitta at center).
// All units in mm. R is the TRUE radius of curvature.
// halfAngle is clamped to at most 85° (1.48 rad) so the arc stays a clean convex shape.
function generateArcPoints(camberRadiusMm, lengthMm, n = 64) {
  const R = camberRadiusMm;
  const halfAngle = Math.min(lengthMm / (2 * R), Math.PI * 0.472);
  const points = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const angle = -halfAngle + t * 2 * halfAngle;
    points.push(
      new THREE.Vector3(
        R * Math.sin(angle),
        R * Math.cos(angle) - R * Math.cos(halfAngle), // 0 at ends, sagitta at center
        0
      )
    );
  }
  return points;
}

// order.camberRadiusMm stores the camber HEIGHT (sag in mm at mid-span), NOT the radius of curvature.
// Example: 0.059ft = 17.98mm sag over ~12m beam → actual R ≈ 979,000mm (979m — very gentle curve).
// This function converts camber height h to actual radius R = (L/2)² / (2h) + h/2,
// then delegates to generateArcPoints so the arc correctly spans the full beam length.
function generateArcFromCamberHeight(camberHeightMm, lengthMm, n = 64) {
  const h = camberHeightMm;
  if (!h || h <= 0 || !lengthMm || lengthMm <= 0) return [];
  const halfL = lengthMm / 2;
  const R = (halfL * halfL) / (2 * h) + h / 2;
  return generateArcPoints(R, lengthMm, n);
}

// Converts order.shoePositions (CSV-derived) into a THREE.Vector3[] arc spine in the X-Y plane.
//
// Data conventions (confirmed from rigidply CSV investigation):
//   - shoePositions: [{position, angle}, ...] in array order (no index field on raw data)
//   - position: distance from the NEAREST beam end, in INCHES. Shoes 0..centerIdx are from
//     the left end (positions increase); shoes centerIdx+1..N-1 are from the right end
//     (positions decrease back toward 0). The center shoe has the maximum position value.
//   - angle: local tangent angle of the beam at that shoe position, in DEGREES.
//     Positive = beam goes upward left-to-right; negative = downward. Center shoe = 0°.
//   - lamellaLengthMm: total beam length in mm from the layer. Pass 0 if unknown — the
//     function will derive total length from 2 × max(position) × 25.4 (inch→mm).
//
// Returns null if fewer than 2 valid entries (caller falls back to camberRadiusMm arc).
function shoePositionsToArcPoints(shoePositions, lamellaLengthMm) {
  if (!shoePositions || shoePositions.length < 2) return null;

  const INCH_TO_MM = 25.4;
  const DEG_TO_RAD = Math.PI / 180;

  // Parse — raw entries only have {position, angle}
  const entries = shoePositions.map((s) => ({ pos: Number(s.position), angleDeg: Number(s.angle) })).filter((e) => Number.isFinite(e.pos) && Number.isFinite(e.angleDeg));

  if (entries.length < 2) return null;

  // Center shoe = entry with maximum position value (farthest from each end)
  let maxPos = -Infinity;
  let centerIdx = 0;
  entries.forEach((e, i) => {
    if (e.pos > maxPos) {
      maxPos = e.pos;
      centerIdx = i;
    }
  });

  // Total beam span is always derived from shoe positions:
  // the center shoe (max position) is by definition at the midpoint, so total = 2 × maxPos × inchToMm.
  // lamellaLengthMm from the layer is a lamella PIECE length (multi-piece beam), not the full span.
  const totalMm = maxPos * 2 * INCH_TO_MM;

  // Convert each shoe to absolute X in mm measured from the left end:
  //   left-half shoes  (i <= centerIdx): X = position × 25.4
  //   right-half shoes (i > centerIdx):  X = totalMm − position × 25.4
  const pts = entries.map((e, i) => ({
    xMm: i <= centerIdx ? e.pos * INCH_TO_MM : totalMm - e.pos * INCH_TO_MM,
    angleDeg: e.angleDeg
  }));

  pts.sort((a, b) => a.xMm - b.xMm);

  // Integrate tan(angle) × dx to get Y elevation at each shoe position.
  // Y=0 at the left end; the arc should peak (positive Y) at mid-span.
  const vec3s = [new THREE.Vector3(pts[0].xMm, 0, 0)];
  let y = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].xMm - pts[i - 1].xMm;
    const avgAngleDeg = (pts[i - 1].angleDeg + pts[i].angleDeg) / 2;
    y += dx * Math.tan(avgAngleDeg * DEG_TO_RAD);
    vec3s.push(new THREE.Vector3(pts[i].xMm, y, 0));
  }

  // Sanity check: peak Y should be positive (camber rises above endpoints).
  // If integration produced a trough instead of a peak, negate all Y values.
  const maxY = Math.max(...vec3s.map((v) => v.y));
  const minY = Math.min(...vec3s.map((v) => v.y));
  if (maxY < Math.abs(minY)) {
    vec3s.forEach((v) => {
      v.y = -v.y;
    });
  }

  // Center the arc around X=0, matching generateArcPoints convention.
  // generateArcPoints produces X in [-halfLength, +halfLength]; camera and scene
  // are calibrated to this convention, so the shoe arc must match.
  const halfLength = totalMm / 2;
  vec3s.forEach((v) => {
    v.x -= halfLength;
  });

  return vec3s;
}

// Returns total beam span in mm derived from shoe positions, or 0 if unavailable.
function getShoePositionsTotalMm(shoePositions) {
  if (!shoePositions || shoePositions.length < 2) return 0;
  let maxPos = -Infinity;
  shoePositions.forEach((s) => {
    const p = Number(s.position);
    if (Number.isFinite(p) && p > maxPos) maxPos = p;
  });
  return maxPos > 0 ? maxPos * 2 * 25.4 : 0;
}

// Trims arcPoints symmetrically so the visible arc represents approximately targetLengthMm.
// No-op if targetLengthMm is 0, >= totalMm, or totalMm is 0.
function trimArcByLength(arcPoints, targetLengthMm, totalMm) {
  if (!arcPoints || arcPoints.length < 2 || targetLengthMm <= 0 || totalMm <= 0 || targetLengthMm >= totalMm) return arcPoints;
  const halfTarget = targetLengthMm / 2;

  // Find first interior index from left, last from right
  const leftIn = arcPoints.findIndex((v) => v.x >= -halfTarget);
  let rightIn = -1;
  for (let i = arcPoints.length - 1; i >= 0; i--) {
    if (arcPoints[i].x <= halfTarget) {
      rightIn = i;
      break;
    }
  }
  if (leftIn === -1 || rightIn === -1 || leftIn > rightIn) return arcPoints;

  const result = [];
  // Interpolate exact left endpoint so it lands precisely at x = -halfTarget
  if (leftIn > 0) {
    const lo = arcPoints[leftIn - 1],
      li = arcPoints[leftIn];
    result.push(new THREE.Vector3().lerpVectors(lo, li, (-halfTarget - lo.x) / (li.x - lo.x)));
  }
  result.push(...arcPoints.slice(leftIn, rightIn + 1));
  // Interpolate exact right endpoint so it lands precisely at x = +halfTarget
  if (rightIn < arcPoints.length - 1) {
    const ri = arcPoints[rightIn],
      ro = arcPoints[rightIn + 1];
    result.push(new THREE.Vector3().lerpVectors(ri, ro, (halfTarget - ri.x) / (ro.x - ri.x)));
  }

  return result.length >= 2 ? result : arcPoints;
}

// Renders a BSH beam as a curved arc derived from shoe positions or (fallback) camberRadiusMm.
// Priority: shoePositions (2+ entries) → camberRadiusMm > 0 → nothing rendered.
// Single mesh for the full beam cross-section — no zone splitting — eliminates all z-fighting
// and Frenet-frame normal artifacts that occur when multiple adjacent meshes share faces.
// All dimensions (camberRadiusMm, lamellaWidth, lamellaThickness, lamellaLength) in mm.
export function CurvedBeamModel({ order, beamOptions = {} }) {
  const camberRadiusMm = Number(order?.camberRadiusMm) || 0;

  const layers = order?.layers || order?.bshLayers || [];
  const bshLayer = layers[0] || null;

  const lamellaWidth = Number(bshLayer?.lamellaWidth) || 300;
  const lamellaThickness = Number(bshLayer?.lamellaThickness) || 30;
  // rawLamellaLength=0 signals "unknown" so shoePositionsToArcPoints derives span from shoes
  const rawLamellaLength = Number(bshLayer?.lamellaLength) || 0;
  const lamellaLength = rawLamellaLength || 10000;
  const numOfLamellas = Number(bshLayer?.numOfLamellas) || 10;
  const totalHeight = numOfLamellas * lamellaThickness;

  const arcPoints = useMemo(() => {
    const shoePts = shoePositionsToArcPoints(order?.shoePositions, rawLamellaLength);
    if (shoePts) return shoePts;
    if (camberRadiusMm && bshLayer) return generateArcFromCamberHeight(camberRadiusMm, lamellaLength);
    return [];
  }, [order?.shoePositions, rawLamellaLength, camberRadiusMm, lamellaLength, bshLayer]);

  const geometry = useMemo(() => {
    if (!arcPoints.length) return null;
    const geo = createCurvedLamellaGeometry({
      points3d: arcPoints,
      crossSectionWidth: lamellaWidth,
      crossSectionHeight: totalHeight,
      geometryRotationX: 0,
      exactPath: true
    });
    if (geo) {
      geo.computeBoundingBox();
      const center = new THREE.Vector3();
      geo.boundingBox.getCenter(center);
      geo.translate(-center.x, -center.y, -center.z);
    }
    return geo;
  }, [arcPoints, lamellaWidth, totalHeight]);

  const material = useMemo(() => new THREE.MeshStandardMaterial({ color: '#A07040', roughness: 0.72, metalness: 0.05 }), []);

  useEffect(() => {
    return () => {
      if (geometry) geometry.dispose();
      if (material) material.dispose();
    };
  }, [geometry, material]);

  if (!geometry || !arcPoints.length) return null;

  return (
    <group scale={[0.001, 0.001, 0.001]}>
      <mesh geometry={geometry} material={material} />
    </group>
  );
}

// Renders all order.layers as separate stacked curved panels (pre-press exploded view).
// All layers share the same base arc so crossSectionCenterY stacks them with correct 0.5mm gaps.
// To show that inner layers are shorter and outer layers are longer, the arc is resampled to
// ARC_RESAMPLE_N points and inner layers have points trimmed symmetrically from both ends.
// Priority: shoePositions (2+ entries) → camberRadiusMm > 0 → nothing rendered.
// Alternating wood-tone colors per layer.
const ARC_RESAMPLE_N = 64;
export function CurvedMultiLayerModel({ order, beamOptions = {} }) {
  const camberRadiusMm = Number(order?.camberRadiusMm) || 0;

  const layers = order?.layers || order?.bshLayers || [];
  const firstLayer = layers[0] || null;

  const rawLamellaLength = Number(firstLayer?.lamellaLength) || 0;
  const lamellaLength = rawLamellaLength || 10000;

  const arcPoints = useMemo(() => {
    const shoePts = shoePositionsToArcPoints(order?.shoePositions, rawLamellaLength);
    if (shoePts) return shoePts;
    if (camberRadiusMm && firstLayer) return generateArcFromCamberHeight(camberRadiusMm, lamellaLength);
    return [];
  }, [order?.shoePositions, rawLamellaLength, camberRadiusMm, lamellaLength, firstLayer]);

  const totalMm = useMemo(() => {
    const shoeTotal = getShoePositionsTotalMm(order?.shoePositions);
    if (shoeTotal > 0) return shoeTotal;
    // No shoe positions: derive beam span from the longest layer so per-layer arc trimming works
    // for camber-only orders (shorter inner layers will appear proportionally shorter).
    const maxLen = Math.max(...layers.map((l) => Number(l.lamellaLength) || 0));
    return maxLen > 0 ? maxLen : 0;
  }, [order?.shoePositions, layers]);

  const geometries = useMemo(() => {
    if (!arcPoints.length || !layers.length) return [];

    const GAP = 0.5;
    const colorA = new THREE.Color('#A07040');
    const colorB = new THREE.Color('#7A4F28');

    const layerHeights = layers.map((l) => (Number(l.numOfLamellas) || 1) * (Number(l.lamellaThickness) || 30));
    const totalStackHeight = layerHeights.reduce((s, h) => s + h, 0) + GAP * (layers.length - 1);

    // Resample shoe arc to ARC_RESAMPLE_N points so trim granularity is consistent
    // regardless of how many shoe positions the order has.
    let baseArc = arcPoints;
    if (arcPoints.length < ARC_RESAMPLE_N) {
      const curve = new THREE.CatmullRomCurve3(arcPoints, false, 'centripetal', 0.5);
      baseArc = curve.getPoints(ARC_RESAMPLE_N - 1);
    }

    const geoms = [];
    let cumulativeBottom = 0;

    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i];
      const h = layerHeights[i];
      const width = Number(layer.lamellaWidth) || 300;
      const thickness = Number(layer.lamellaThickness) || 30;
      const numLamellas = Number(layer.numOfLamellas) || 1;

      const MAX_STRIPS = 24;
      const groupSize = numLamellas > MAX_STRIPS ? Math.ceil(numLamellas / MAX_STRIPS) : 1;
      const numStrips = Math.ceil(numLamellas / groupSize);
      const stripThickness = groupSize * thickness;
      const layerBottomY = cumulativeBottom - totalStackHeight / 2;

      // Trim each layer's arc to its own lamellaLength so the assembled view shows the
      // graduated length decrease (longer outer layers extend past shorter inner layers).
      const layerArc = trimArcByLength(baseArc, Number(layer.lamellaLength) || 0, totalMm);
      if (layerArc.length < 2) {
        cumulativeBottom += h + GAP;
        continue;
      }

      for (let j = 0; j < numStrips; j++) {
        const stripCenterY = layerBottomY + j * stripThickness + stripThickness / 2;
        const geo = createCurvedLamellaGeometry({
          points3d: layerArc,
          crossSectionWidth: width,
          crossSectionHeight: stripThickness - 0.5,
          crossSectionCenterY: stripCenterY,
          geometryRotationX: 0,
          exactPath: true
        });
        if (!geo) continue;

        const c = j % 2 === 0 ? colorA : colorB;
        const count = geo.attributes.position.count;
        const colors = new Float32Array(count * 3);
        for (let v = 0; v < count; v++) {
          colors[v * 3] = c.r;
          colors[v * 3 + 1] = c.g;
          colors[v * 3 + 2] = c.b;
        }
        geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        geoms.push(geo);
      }
      cumulativeBottom += h + GAP;
    }

    if (geoms.length) {
      const box = new THREE.Box3();
      geoms.forEach((g) => {
        g.computeBoundingBox();
        box.union(g.boundingBox);
      });
      const center = new THREE.Vector3();
      box.getCenter(center);
      geoms.forEach((g) => g.translate(-center.x, -center.y, -center.z));
    }

    return geoms;
  }, [arcPoints, layers, totalMm]);

  const material = useMemo(() => new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.72, metalness: 0.05 }), []);

  useEffect(() => {
    return () => {
      geometries.forEach((g) => g.dispose());
      material.dispose();
    };
  }, [geometries, material]);

  if (!geometries.length || !arcPoints.length) return null;

  return (
    <group scale={[0.001, 0.001, 0.001]}>
      {geometries.map((geo, i) => (
        <mesh key={i} geometry={geo} material={material} />
      ))}
    </group>
  );
}

// Renders all order.layers as straight stacked lamella strips (pre-press exploded view) for
// orders with no camber and no shoe positions. Same visual language as CurvedMultiLayerModel:
// layers stacked in Y with 0.5mm gaps, MAX_STRIPS grouping, alternating wood tones, per-layer
// length staircase (shorter layers end symmetrically before longer ones), bbox-centered, mm at
// scale 0.001. Built from BoxGeometry instead of createCurvedLamellaGeometry — extruding a
// colinear spine through ExtrudeGeometry produces degenerate Frenet frames (the same collapse
// that forced the display-camber boost in BSHOrderPanelModal), so straight beams must not go
// through the curved extrusion path.
export function StraightMultiLayerModel({ order, beamOptions = {} }) {
  const layers = order?.layers || order?.bshLayers || [];

  const geometries = useMemo(() => {
    if (!layers.length) return [];

    const GAP = 0.5;
    const colorA = new THREE.Color('#A07040');
    const colorB = new THREE.Color('#7A4F28');

    const maxLen = Math.max(0, ...layers.map((l) => Number(l.lamellaLength) || 0)) || 10000;
    const layerHeights = layers.map((l) => (Number(l.numOfLamellas) || 1) * (Number(l.lamellaThickness) || 30));
    const totalStackHeight = layerHeights.reduce((s, h) => s + h, 0) + GAP * (layers.length - 1);

    const geoms = [];
    let cumulativeBottom = 0;

    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i];
      const h = layerHeights[i];
      const width = Number(layer.lamellaWidth) || 300;
      const thickness = Number(layer.lamellaThickness) || 30;
      const numLamellas = Number(layer.numOfLamellas) || 1;
      const length = Number(layer.lamellaLength) || maxLen;

      const MAX_STRIPS = 24;
      const groupSize = numLamellas > MAX_STRIPS ? Math.ceil(numLamellas / MAX_STRIPS) : 1;
      const numStrips = Math.ceil(numLamellas / groupSize);
      const stripThickness = groupSize * thickness;
      const layerBottomY = cumulativeBottom - totalStackHeight / 2;

      for (let j = 0; j < numStrips; j++) {
        const stripCenterY = layerBottomY + j * stripThickness + stripThickness / 2;
        const geo = new THREE.BoxGeometry(length, stripThickness - 0.5, width);
        geo.translate(0, stripCenterY, 0);

        const c = j % 2 === 0 ? colorA : colorB;
        const count = geo.attributes.position.count;
        const colors = new Float32Array(count * 3);
        for (let v = 0; v < count; v++) {
          colors[v * 3] = c.r;
          colors[v * 3 + 1] = c.g;
          colors[v * 3 + 2] = c.b;
        }
        geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        geoms.push(geo);
      }
      cumulativeBottom += h + GAP;
    }

    if (geoms.length) {
      const box = new THREE.Box3();
      geoms.forEach((g) => {
        g.computeBoundingBox();
        box.union(g.boundingBox);
      });
      const center = new THREE.Vector3();
      box.getCenter(center);
      geoms.forEach((g) => g.translate(-center.x, -center.y, -center.z));
    }

    return geoms;
  }, [layers]);

  const material = useMemo(() => new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.72, metalness: 0.05 }), []);

  useEffect(() => {
    return () => {
      geometries.forEach((g) => g.dispose());
      material.dispose();
    };
  }, [geometries, material]);

  if (!geometries.length) return null;

  return (
    <group scale={[0.001, 0.001, 0.001]}>
      {geometries.map((geo, i) => (
        <mesh key={i} geometry={geo} material={material} />
      ))}
    </group>
  );
}

// Renders a single BSH layer as individual curved lamella strips stacked along the arc.
// Priority: shoePositions (2+ entries) → camberRadiusMm > 0 → nothing rendered.
// All strips share the same base arc path — only the cross-section center shifts per strip.
// Because every strip is extruded along the identical CatmullRom curve, adjacent strip faces
// are perfectly parallel (no path divergence, no intersection, no z-fighting).
// Caps at 24 visible strips (groups lamellas if count exceeds 24).
export function CurvedLayerModel({ order, layer, beamOptions = {} }) {
  const camberRadiusMm = Number(order?.camberRadiusMm) || 0;

  const lamellaWidth = Number(layer?.lamellaWidth) || 300;
  const lamellaThickness = Number(layer?.lamellaThickness) || 30;
  // rawLamellaLength=0 signals "unknown" so shoePositionsToArcPoints derives span from shoes
  const rawLamellaLength = Number(layer?.lamellaLength) || 0;
  const lamellaLength = rawLamellaLength || 10000;
  const rawCount = Number(layer?.numOfLamellas) || 1;

  const MAX_STRIPS = 24;
  const groupSize = rawCount > MAX_STRIPS ? Math.ceil(rawCount / MAX_STRIPS) : 1;
  const numStrips = Math.ceil(rawCount / groupSize);
  const stripThickness = groupSize * lamellaThickness;
  const totalHeight = rawCount * lamellaThickness;

  // Single shared arc — trimmed to layer.lamellaLength so each layer tab shows its own proportional span.
  const arcPoints = useMemo(() => {
    const shoePts = shoePositionsToArcPoints(order?.shoePositions, rawLamellaLength);
    if (shoePts) {
      if (rawLamellaLength > 0) {
        return trimArcByLength(shoePts, rawLamellaLength, getShoePositionsTotalMm(order?.shoePositions));
      }
      return shoePts;
    }
    if (camberRadiusMm && layer) return generateArcFromCamberHeight(camberRadiusMm, lamellaLength);
    return [];
  }, [order?.shoePositions, rawLamellaLength, camberRadiusMm, lamellaLength, layer]);

  // Each strip is built from the SAME arcPoints with crossSectionCenterY offsetting the cross-section
  // in the shape's local Y — no arc-path shifting, so CatmullRom produces identical interpolation
  // for every strip, eliminating inter-strip face divergence and z-fighting.
  const geometries = useMemo(() => {
    if (!arcPoints.length) return [];

    const colorA = new THREE.Color('#A07040');
    const colorB = new THREE.Color('#7A4F28');
    const effectiveThickness = stripThickness - 0.5;

    const geoms = [];
    for (let i = 0; i < numStrips; i++) {
      const centerY = i * stripThickness - totalHeight / 2 + stripThickness / 2;
      const geo = createCurvedLamellaGeometry({
        points3d: arcPoints,
        crossSectionWidth: lamellaWidth,
        crossSectionHeight: effectiveThickness,
        crossSectionCenterY: centerY,
        geometryRotationX: 0,
        exactPath: true
      });
      if (!geo) continue;

      const c = i % 2 === 0 ? colorA : colorB;
      const count = geo.attributes.position.count;
      const colors = new Float32Array(count * 3);
      for (let v = 0; v < count; v++) {
        colors[v * 3] = c.r;
        colors[v * 3 + 1] = c.g;
        colors[v * 3 + 2] = c.b;
      }
      geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      geoms.push(geo);
    }

    if (geoms.length) {
      const box = new THREE.Box3();
      geoms.forEach((g) => {
        g.computeBoundingBox();
        box.union(g.boundingBox);
      });
      const center = new THREE.Vector3();
      box.getCenter(center);
      geoms.forEach((g) => g.translate(-center.x, -center.y, -center.z));
    }

    return geoms;
  }, [arcPoints, lamellaWidth, stripThickness, numStrips, totalHeight]);

  const material = useMemo(() => new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.72, metalness: 0.05 }), []);

  useEffect(() => {
    return () => {
      geometries.forEach((g) => g.dispose());
      material.dispose();
    };
  }, [geometries, material]);

  if (!geometries.length || !arcPoints.length) return null;

  return (
    <group scale={[0.001, 0.001, 0.001]}>
      {geometries.map((geo, i) => (
        <mesh key={i} geometry={geo} material={material} />
      ))}
    </group>
  );
}
